import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Psl } from '../../types/common/entities.ts';

export const SIDEBAR_MIN_WIDTH = 240;
export const SIDEBAR_MAX_WIDTH = 460;
export const SIDEBAR_DEFAULT_WIDTH = 288;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

export interface AppState {
  selectedPsl: Psl | null;
  sidebarCollapsed: boolean;
  sidebarWidth: number;
  sidebarMobileOpen: boolean;
  availablePsls: Psl[];
}

function clampWidth(width: number): number {
  return Number.isFinite(width) ? Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(width))) : SIDEBAR_DEFAULT_WIDTH;
}

export function readStoredApp(): AppState {
  const initial: AppState = { selectedPsl: null, sidebarCollapsed: false, sidebarWidth: SIDEBAR_DEFAULT_WIDTH, sidebarMobileOpen: false, availablePsls: [] };
  try {
    const stored = JSON.parse(localStorage.getItem('qcdoc-app-store') ?? 'null')?.state as Partial<AppState> | undefined;
    if (stored) {
      initial.selectedPsl = stored.selectedPsl ?? null;
      initial.sidebarCollapsed = stored.sidebarCollapsed === true;
      initial.sidebarWidth = clampWidth(stored.sidebarWidth ?? SIDEBAR_DEFAULT_WIDTH);
    }
  } catch {
    return initial;
  }
  return initial;
}

const appSlice = createSlice({
  name: 'common/app',
  initialState: readStoredApp,
  reducers: {
    setSelectedPsl(state, action: PayloadAction<Psl | null>) { state.selectedPsl = action.payload; },
    setSidebarCollapsed(state, action: PayloadAction<boolean>) { state.sidebarCollapsed = action.payload; },
    setSidebarWidth(state, action: PayloadAction<number>) { state.sidebarWidth = clampWidth(action.payload); },
    toggleSidebar(state) { state.sidebarCollapsed = !state.sidebarCollapsed; },
    setSidebarMobileOpen(state, action: PayloadAction<boolean>) { state.sidebarMobileOpen = action.payload; },
    toggleMobileSidebar(state) { state.sidebarMobileOpen = !state.sidebarMobileOpen; },
    setAvailablePsls(state, action: PayloadAction<Psl[]>) { state.availablePsls = action.payload; },
    patch(state, action: PayloadAction<Partial<AppState>>) { Object.assign(state, action.payload); },
  },
});

export const appActions = appSlice.actions;
export default appSlice.reducer;